// assets/AllCourses/Exercises/SentenceJumblesExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function SentenceJumblesExercise({
  exercise = {},
  onUpdate,            // Edit mode only
  errors = {},
  isPreview = false,   // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.sentenceJumblesQuestions)
    ? exercise.sentenceJumblesQuestions.map((q) => ({
        correctSentence:
          typeof q.correctSentence === "string"
            ? q.correctSentence.trim()
            : "",
        jumbledWords: Array.isArray(q.jumbledWords)
          ? q.jumbledWords.map((w) => String(w).trim())
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    activeIndex: 0,
    answers: {}, // { [qIndex]: { order: number[] } }
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= SHUFFLED WORDS (STABLE) ================= */
  const [shuffledWords, setShuffledWords] = useState([]);

  const currentQuestion = questions[state.activeIndex];

  useEffect(() => {
    if (!currentQuestion) {
      setShuffledWords([]);
      return;
    }

    const wordsWithIndex = currentQuestion.jumbledWords.map((word, idx) => ({
      word,
      id: idx,
    }));

    setShuffledWords(wordsWithIndex.sort(() => Math.random() - 0.5));
  }, [state.activeIndex, questions]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text).trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  const userOrder = state.answers[state.activeIndex]?.order || [];

  /* ================= EDIT MODE HELPERS ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("sentenceJumblesQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      [field]: typeof value === "string" ? value.trim() : value,
    };
    updateQuestions(next);
  };

  const handleJumbledWordChange = (qIdx, wIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx];
    const words = [...q.jumbledWords];
    words[wIdx] = typeof value === "string" ? value.trim() : value;
    next[qIdx] = { ...q, jumbledWords: words };
    updateQuestions(next);
  };

  const handleAddJumbledWord = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      jumbledWords: [...next[qIdx].jumbledWords, ""],
    };
    updateQuestions(next);
  };

  const handleRemoveJumbledWord = (qIdx, wIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      jumbledWords: next[qIdx].jumbledWords.filter((_, i) => i !== wIdx),
    };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { correctSentence: "", jumbledWords: [], requireCapitalization: false },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleDragStart = (e, id) => {
    if (!isPreview) return;
    e.dataTransfer.setData("text/plain", String(id));
  };

  const handleDrop = (e) => {
    if (!isPreview || showResult) return;
    e.preventDefault();

    const id = Number(e.dataTransfer.getData("text/plain"));
    if (Number.isNaN(id) || userOrder.includes(id)) return;

    const newOrder = [...userOrder, id];

    setState((prev) => {
      const next = {
        ...prev,
        answers: {
          ...prev.answers,
          [state.activeIndex]: { order: newOrder },
        },
        hasInteracted: true,
      };

      const builtSentence = newOrder
        .map((i) => currentQuestion.jumbledWords[i])
        .join(" ");

      const isComplete =
        newOrder.length === currentQuestion.jumbledWords.length;

      const isCorrect = isComplete
        ? normalize(builtSentence, currentQuestion.requireCapitalization) ===
          normalize(
            currentQuestion.correctSentence,
            currentQuestion.requireCapitalization
          )
        : false;

      if (
        isComplete &&
        state.activeIndex === questions.length - 1 &&
        !completedRef.current
      ) {
        completedRef.current = true;
        onComplete(exercise._id, isCorrect);
      }

      return next;
    });
  };

  const handleDragOver = (e) => e.preventDefault();

  const goNext = () => {
    if (state.activeIndex >= questions.length - 1) return;
    setState((prev) => ({
      ...prev,
      activeIndex: prev.activeIndex + 1,
    }));
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Correct Sentence ${qIdx + 1}`}
              value={q.correctSentence}
              onChange={(v) =>
                handleQuestionChange(qIdx, "correctSentence", v)
              }
              placeholder="The cat is sleeping on the mat."
              required
              error={
                errors?.[
                  `exercise-${exercise?.index ?? qIdx}-sj-correct-${qIdx}`
                ]
              }
            />

            <div className="space-y-3">
              <label className="font-medium">Jumbled Words</label>
              {q.jumbledWords.map((word, wIdx) => (
                <div key={wIdx} className="flex gap-3 items-end">
                  <StringField
                    value={word}
                    onChange={(v) =>
                      handleJumbledWordChange(qIdx, wIdx, v)
                    }
                    placeholder="Word"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      handleRemoveJumbledWord(qIdx, wIdx)
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddJumbledWord(qIdx)}
              >
                Add Word
              </Button>
            </div>

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) => handleCapitalizationChange(qIdx, c)}
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT RENDER ================= */
  if (!currentQuestion) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No sentence jumble questions found.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const builtSentence = userOrder
    .map((i) => currentQuestion.jumbledWords[i])
    .join(" ");

  const isComplete =
    userOrder.length === currentQuestion.jumbledWords.length;

  const isCorrect = isComplete
    ? normalize(builtSentence, currentQuestion.requireCapitalization) ===
      normalize(
        currentQuestion.correctSentence,
        currentQuestion.requireCapitalization
      )
    : false;

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>
          Rearrange the words • Question {state.activeIndex + 1} of{" "}
          {questions.length}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        <div className="flex flex-wrap justify-center gap-4 p-6 bg-muted rounded-xl">
          {shuffledWords.map(({ word, id }) => (
            <Button
              key={id}
              draggable
              onDragStart={(e) => handleDragStart(e, id)}
              disabled={userOrder.includes(id) || showResult}
              variant="secondary"
              className={cn(
                "cursor-grab text-lg px-6 py-4",
                userOrder.includes(id) && "opacity-50"
              )}
            >
              {word}
            </Button>
          ))}
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={cn(
            "min-h-32 border-4 border-dashed rounded-2xl p-8 text-center",
            isComplete && isCorrect && "border-green-600 bg-green-50",
            isComplete && !isCorrect && "border-red-600 bg-red-50"
          )}
        >
          {userOrder.length === 0 ? (
            <p className="text-gray-500 text-lg">
              Drag words here to build the sentence
            </p>
          ) : (
            <p className="text-2xl font-bold">{builtSentence}</p>
          )}
        </div>

        {showResult && isComplete && (
          <div className="text-center">
            <p
              className={cn(
                "text-3xl font-bold",
                isCorrect ? "text-green-700" : "text-red-700"
              )}
            >
              {isCorrect ? "Correct!" : "Not quite right"}
            </p>
            {!isCorrect && (
              <p className="mt-4 text-xl">
                Correct sentence:{" "}
                <strong>{currentQuestion.correctSentence}</strong>
              </p>
            )}
          </div>
        )}

        {isComplete && state.activeIndex < questions.length - 1 && (
          <div className="text-center">
            <Button size="lg" onClick={goNext}>
              Next Question →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
